import { useLocaleContext } from "@app/contexts/LocaleContext";

/**
 * Privacy policy, in French and in English. The English text is a translation for the physicians
 * reading the platform in English (and in German or Spanish, which have no version of their own);
 * the French one is the reference. Any change to one must be carried to the other.
 */
export default function PrivacyPolicy() {
  const { locale } = useLocaleContext();
  return locale === "fr" ? <PrivacyPolicyFr /> : <PrivacyPolicyEn />;
}

function PrivacyPolicyFr() {
  return (
    <div className="max-h-full overflow-auto p-10">
      <h1 className="-ml-2 mb-2 text-2xl font-bold">
        Politique de Confidentialité
      </h1>
      <p>
        <strong>Dernière mise à jour :</strong> 24/03/2026
      </p>

      <h2>1. Responsable du traitement</h2>
      <p>
        Le responsable du traitement des données personnelles collectées via la
        plateforme est :
      </p>
      <ul>
        <li>
          <strong>Noms :</strong> Augustin Missoffe et François Masson
        </li>
        <li>
          <strong>Contact :</strong>{" "}
          <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>
        </li>
      </ul>

      <h2>2. Données collectées</h2>
      <p>
        Nous collectons uniquement les données nécessaires au bon fonctionnement
        de la plateforme :
      </p>
      <ul>
        <li>
          <strong>Identifiant :</strong> Adresse e-mail fournie lors de
          l'inscription.
        </li>
        <li>
          <strong>Données personnelles :</strong> nom, prénom, photo de profil,
          établissement, spécialité, status, lieu d'exercice (facultatif), RPPS
          (facultatif)
        </li>
        <li>
          <strong>Cookies :</strong> Un cookie d'authentification pour gérer
          votre session.
        </li>
      </ul>

      <h2>3. Finalités du traitement</h2>
      <p>Les données collectées sont utilisées exclusivement pour :</p>
      <ul>
        <li>Créer et gérer votre compte utilisateur.</li>
        <li>
          Vérifier votre identité et votre statut de professionnel de santé.
        </li>
        <li>Assurer un accès sécurisé et performant à la plateforme.</li>
      </ul>

      <h2>4. Absence de partage avec des tiers</h2>
      <p>
        Vos données personnelles ne sont partagées avec aucune tierce partie.
        Elles sont exclusivement utilisées par MAGE-X pour les finalités
        décrites ci-dessus.
      </p>

      <h2>5. Durée de conservation</h2>
      <p>
        Vos données sont conservées tant que votre compte reste actif. Une fois
        votre compte supprimé, les données associées sont également supprimées,
        sauf en cas d'obligation légale.
      </p>

      <h2>6. Sécurité des données</h2>
      <p>
        Nous mettons en œuvre des mesures de sécurité techniques et
        organisationnelles pour protéger vos données :
      </p>
      <ul>
        <li>Chiffrement des données sensibles.</li>
        <li>
          Stockage sécurisé sur des serveurs conformes aux normes applicables.
        </li>
        <li>Accès limité aux seuls personnels autorisés.</li>
      </ul>

      <h2>7. Vos droits</h2>
      <p>
        Conformément à la réglementation, vous disposez des droits suivants :
      </p>
      <ul>
        <li>
          <strong>Droit d'accès :</strong> Obtenir une copie de vos données
          personnelles.
        </li>
        <li>
          <strong>Droit de rectification :</strong> Modifier vos données
          incorrectes ou incomplètes.
        </li>
        <li>
          <strong>Droit à l'effacement :</strong> Demander la suppression de vos
          données.
        </li>
        <li>
          <strong>Droit d'opposition :</strong> Refuser le traitement de vos
          données dans certains cas.
        </li>
      </ul>
      <p>
        Pour exercer vos droits, contactez-nous à l'adresse suivante :{" "}
        <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>
      <p>
        Pour supprimer votre compte, veuillez vous connecter, aller dans
        "modifier mon profil" et faire défiler vers le bas pour sélectionner
        "supprimer mon compte".
      </p>

      <h2>8. Utilisation des cookies</h2>
      <p>
        Nous utilisons uniquement un cookie d'authentification pour gérer votre
        session sur la plateforme. Ce cookie est indispensable et ne collecte
        aucune donnée supplémentaire.
      </p>

      <h2>9. Modifications de la politique de confidentialité</h2>
      <p>
        Cette politique de confidentialité peut être mise à jour pour refléter
        des changements dans nos pratiques ou obligations légales. Toute
        modification sera communiquée via la plateforme.
      </p>

      <h2>10. Contact</h2>
      <p>
        Pour toute question ou réclamation relative à cette politique,
        contactez-nous à :{" "}
        <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>

      <h2>11. Normes de sécurité pour les enfants</h2>
      <p>
        MAGE-X s'engage à respecter les normes de sécurité pour les enfants.
        Nous avons mis en place des politiques strictes pour prévenir les abus
        et l'exploitation sexuelle des enfants (CSAE). Nos normes incluent :
      </p>
      <ul>
        <li>
          Formation régulière de notre personnel sur la détection et la
          prévention des abus.
        </li>
        <li>
          Surveillance proactive des interactions sur la plateforme pour
          identifier les comportements suspects.
        </li>
        <li>
          Outils de signalement faciles à utiliser pour permettre aux
          utilisateurs de signaler des préoccupations.
        </li>
        <li>
          Collaboration avec des organisations spécialisées dans la protection
          des enfants pour améliorer nos pratiques.
        </li>
      </ul>
      <p>
        Pour toute question ou préoccupation concernant la sécurité des enfants,
        veuillez contacter notre point de contact à l'adresse suivante :{" "}
        <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>
      <p>
        MAGE-X permet aux utilisateurs de signaler des préoccupations concernant
        la sécurité des enfants via l'application. Pour en savoir plus sur les
        exigences de signalement, visitez notre Centre d'aide.
      </p>
      <p>
        Notre application respecte toutes les lois pertinentes en matière de
        sécurité des enfants et signale aux autorités régionales et nationales.
        Pour en savoir plus sur le signalement aux autorités compétentes,
        visitez notre Centre d'aide.
      </p>
    </div>
  );
}

function PrivacyPolicyEn() {
  return (
    <div className="max-h-full overflow-auto p-10">
      <h1 className="-ml-2 mb-2 text-2xl font-bold">Privacy Policy</h1>
      <p>
        <strong>Last updated:</strong> 24 March 2026
      </p>
      <p>
        <em>
          This English version is a translation provided for convenience. In
          case of discrepancy, the French version prevails.
        </em>
      </p>

      <h2>1. Data controller</h2>
      <p>
        The controller of the personal data collected through the platform is:
      </p>
      <ul>
        <li>
          <strong>Names:</strong> Augustin Missoffe and François Masson
        </li>
        <li>
          <strong>Contact:</strong>{" "}
          <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>
        </li>
      </ul>

      <h2>2. Data collected</h2>
      <p>
        We only collect the data necessary for the proper functioning of the
        platform:
      </p>
      <ul>
        <li>
          <strong>Identifier:</strong> Email address provided at registration.
        </li>
        <li>
          <strong>Personal data:</strong> last name, first name, profile
          picture, institution, specialty, status, place of practice (optional),
          RPPS number (optional)
        </li>
        <li>
          <strong>Cookies:</strong> An authentication cookie to manage your
          session.
        </li>
      </ul>

      <h2>3. Purposes of processing</h2>
      <p>The data collected is used exclusively to:</p>
      <ul>
        <li>Create and manage your user account.</li>
        <li>
          Verify your identity and your status as a healthcare professional.
        </li>
        <li>Provide secure and efficient access to the platform.</li>
      </ul>

      <h2>4. No sharing with third parties</h2>
      <p>
        Your personal data is not shared with any third party. It is used
        exclusively by MAGE-X for the purposes described above.
      </p>

      <h2>5. Retention period</h2>
      <p>
        Your data is kept for as long as your account remains active. Once your
        account is deleted, the associated data is also deleted, unless required
        by law.
      </p>

      <h2>6. Data security</h2>
      <p>
        We implement technical and organisational security measures to protect
        your data:
      </p>
      <ul>
        <li>Encryption of sensitive data.</li>
        <li>
          Secure storage on servers that comply with applicable standards.
        </li>
        <li>Access restricted to authorised personnel only.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>In accordance with the regulations, you have the following rights:</p>
      <ul>
        <li>
          <strong>Right of access:</strong> Obtain a copy of your personal data.
        </li>
        <li>
          <strong>Right to rectification:</strong> Correct inaccurate or
          incomplete data.
        </li>
        <li>
          <strong>Right to erasure:</strong> Request the deletion of your data.
        </li>
        <li>
          <strong>Right to object:</strong> Refuse the processing of your data
          in certain cases.
        </li>
      </ul>
      <p>
        To exercise your rights, contact us at:{" "}
        <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>
      <p>
        To delete your account, log in, go to "edit my profile" and scroll down
        to select "delete my account".
      </p>

      <h2>8. Use of cookies</h2>
      <p>
        We only use an authentication cookie to manage your session on the
        platform. This cookie is essential and collects no additional data.
      </p>

      <h2>9. Changes to the privacy policy</h2>
      <p>
        This privacy policy may be updated to reflect changes in our practices
        or legal obligations. Any change will be communicated through the
        platform.
      </p>

      <h2>10. Contact</h2>
      <p>
        For any question or complaint regarding this policy, contact us at:{" "}
        <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>

      <h2>11. Child safety standards</h2>
      <p>
        MAGE-X is committed to child safety standards. We have put in place
        strict policies to prevent child sexual abuse and exploitation (CSAE).
        Our standards include:
      </p>
      <ul>
        <li>
          Regular training of our staff on detecting and preventing abuse.
        </li>
        <li>
          Proactive monitoring of interactions on the platform to identify
          suspicious behaviour.
        </li>
        <li>Easy-to-use reporting tools allowing users to report concerns.</li>
        <li>
          Collaboration with organisations specialised in child protection to
          improve our practices.
        </li>
      </ul>
      <p>
        For any question or concern about child safety, please contact our point
        of contact at: <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>
      <p>
        MAGE-X allows users to report child safety concerns through the app. To
        learn more about reporting requirements, visit our Help Centre.
      </p>
      <p>
        Our app complies with all relevant child safety laws and reports to
        regional and national authorities. To learn more about reporting to the
        competent authorities, visit our Help Centre.
      </p>
    </div>
  );
}
